import React, { useState, useEffect } from "react";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { FilterTypes, ColumnTypes } from "commons/table/Constants";
import { Container, Card } from 'react-bootstrap';
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { hasRoleIn } from 'rbac/RbacHelper';
import PERMISSIONS from "rbac/Permissions.json";
import UpdatePrescriberInfo from "components/UpdatePrescriberInfo";

const PrescriberListV2 = ({
  setSelectedPrescribers = (prescriberIds) => {},
  values,
  setValues,
  resetTableSelection = false,
  dataTestId = "prescriber-list",
}) => {
  const [prescribers, setPrescribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedPrescriber, setSelectedPrescriber] = useState(null);
  const [autoOpenModal, setAutoOpenModal] = useState(false);
  const [listState, setListState] = useState({
    page: 0,
    pageSize: 5,
    sorted: [],
    filtered: [],
  });

  const handleNpiClick = (prescriberIdentifier, row) => {
    // First try to use row data if it has all necessary fields
    const rowData = row.original;
    const prescriberInfoUuid = rowData.prescriberInfoUuid || prescriberIdentifier;

    if (prescriberInfoUuid && rowData.prescriberFirstName && rowData.prescriberLastName) {
      // Use row data if it has all required fields
      setSelectedPrescriber({ ...rowData, prescriberInfoUuid });
      setAutoOpenModal(true);
      return;
    }

    // Otherwise, fetch full prescriber details using prescriberInfoUuid
    if (prescriberInfoUuid) {
      sendAuthenticatedAsyncRequest(
        `/contact-management/prescriber-info/${prescriberInfoUuid}`,
        "GET",
        null,
        (r) => {
          setSelectedPrescriber(r.data);
          setAutoOpenModal(true);
        },
        (r) => {
          // If fetch fails, try using row data as fallback
          if (rowData.prescriberInfoUuid || prescriberIdentifier) {
            setSelectedPrescriber({ ...rowData, prescriberInfoUuid: prescriberInfoUuid });
            setAutoOpenModal(true);
          }
        }
      );
    }
  };

  const columns = [
    {
      header: "Prescriber Name",
      accessor: "prescriberName",
      accessorKey: "prescriberName",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Prescriber Npi",
      accessor: "prescriberNpi",
      accessorKey: "prescriberNpi",
      width: 150,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        const prescriberInfoUuid = row.original.prescriberInfoUuid || row.original.prescriberId;
        if (!prescriberInfoUuid || !value) {
          return <span>{value || ""}</span>;
        }
        return (
          <span
            style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
            onClick={(e) => {
              e.stopPropagation();
              handleNpiClick(prescriberInfoUuid, row);
            }}
            title="Click to edit prescriber details"
          >
            {value}
          </span>
        );
      },
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Prescriber Phone",
      accessor: "prescriberPhone",
      accessorKey: "prescriberPhone",
      width: 150,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Prescriber Fax",
      accessor: "prescriberFax",
      accessorKey: "prescriberFax",
      width: 150,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Prescriber Address",
      accessor: "prescriberPractice",
      accessorKey: "prescriberPractice",
      width: 250,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
  ];

  const fetchPrescribers = (state) => {
    setLoading(true);
    sendAuthenticatedAsyncRequest(
      `/contact-management/prescribers`,
      "POST",
      state,
      (r) => {
        setTotalPages(r.data.totalPages);
        setTotalElements(r.data.totalElements);
        setPrescribers(r.data.records);
        setLoading(false);
      },
      (r) => {
        setTotalPages(0);
        setTotalElements(0);
        setPrescribers([]);
        setLoading(false);
      }
    );
  };

  const resetPrescriberList = () => {
    setPrescribers([]);
    setTotalPages(0);
    setTotalElements(0);
    setLoading(false);
    setListState({
      page: 0,
      pageSize: 5,
      sorted: [],
      filtered: [],
    });
  };

  useEffect(() => {
    return () => {
      resetPrescriberList();
    };
  }, []);

  // Reset autoOpenModal after it's been used
  useEffect(() => {
    if (autoOpenModal) {
      // Reset after a short delay to allow the modal to open
      const timer = setTimeout(() => {
        setAutoOpenModal(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoOpenModal]);


  const onUpdateTable = (filterValues, sortBy, pageSize, pageNo) => {
    if (
      filterValues.length === 1 &&
      (filterValues[0].value === "DEFAULT_FILTER" || filterValues[0].isDefault)
    ) {
      resetPrescriberList();
    } else {
      const newState = {
        page: pageNo,
        pageSize: pageSize,
        sorted: sortBy ? sortBy : [],
        filtered: filterValues,
      };
      setListState(newState);
      fetchPrescribers(newState);
    }
  };

  const onRowSelect = (selectedRows) => {
    setSelectedPrescribers(Object.keys(selectedRows), values, setValues);
  };

  const handlePrescriberUpdate = () => {
    // Refresh the prescriber list after update
    if (listState.filtered.length > 0 &&
        !(listState.filtered.length === 1 &&
          (listState.filtered[0].value === "DEFAULT_FILTER" || listState.filtered[0].isDefault))) {
      fetchPrescribers(listState);
    }
    setSelectedPrescriber(null);
    setAutoOpenModal(false);
  };

  const handlePrescriberSubmission = (updatedPrescriber) => {
    setPrescribers((prevPrescribers) =>
      prevPrescribers.map((p) =>
        p.prescriberInfoUuid === updatedPrescriber.prescriberInfoUuid
          ? updatedPrescriber
          : p
      )
    );
  };

  const handleModalClose = () => {
    setSelectedPrescriber(null);
    setAutoOpenModal(false);
  };

  return !hasRoleIn([PERMISSIONS.PERM_PHI_ACCESS]) ? (
    <Container className="list-container mt-3" fluid>
      <Card variant="primary" className={"mb-3"}>
        <Card.Title className="text-center">
          <h5>User does not have PHI access to view Prescriber List</h5>
        </Card.Title>
      </Card>
    </Container>
  ) : (
    <>
      <ServerSideReactTable
        columnDefination={columns}
        data={prescribers}
        currentPage={listState.page}
        sorted={listState.sorted}
        filters={listState.filtered}
        totalPages={totalPages}
        totalRecords={totalElements || 0}
        loading={loading}
        enableMultiRowSelection={true}
        onRowSelect={onRowSelect}
        overrideRowId={"prescriberNpi"}
        enableAllRowSelect={false}
        addRowSelection={true}
        defaultPageSize={5}
        pageSizeOptions={[5]}
        onUpdateTableData={onUpdateTable}
        resetTableSelection={resetTableSelection}
        dataTestId={dataTestId}
      />
      {selectedPrescriber && (
        <UpdatePrescriberInfo
          prescriber={selectedPrescriber}
          isDisabled={false}
          onSubmission={handlePrescriberSubmission}
          updateData={handlePrescriberUpdate}
          isFromPrescriberProfileList={true}
          autoOpen={autoOpenModal}
          onClose={handleModalClose}
          hideOpenButton={true}
        />
      )}
    </>
  );
};

export default PrescriberListV2;
